import { FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { VideoRecordType } from '@distribute/shared/api-types/video-record';

import {
  Table,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from '../../../../shared/ui/TableV2';

import {
  checkIsRecordConverting,
  checkIsRecordConvertingFailed,
} from '../../lib';

import { RecordCategory } from '../../../../entities/recordings';

import {
  VideoCardPreview,
  VideoCardTablePreview,
  VideoStatus,
  VideoStatusShared,
} from '../../../../entities/video';
import { formatMsToTimePassed, getMuxImage } from '@distribute/shared/utils';
import {
  Avatar,
  AvatarTooltipContent,
  TimeAgo,
  Tooltip,
} from '../../../../shared/ui';
import {
  UserPermissionToVideoRecordRole,
  VideoRecordTeamMembersPermission,
} from '@distribute/shared/types';
import { getRecordPermissions } from '../../hooks';

import { authUserModel } from '../../../../entities/auth-user';
import { RecordDropdown } from './RecordDropdown';
import { generatePath } from 'react-router-dom';

import { QUICK_RECORDING_ROUTE } from '../../../../entities/history';
import { navigate } from '../../../../processes/navigation';

type Props = {
  records: VideoRecordType[];
  category: RecordCategory;
};

export const RecordsGrid: FC<Props> = ({ records, category }) => {
  const user = useSelector(authUserModel.selectors.selectUserWithError);

  const dispatch = useDispatch();

  const handleOpen = (prefix: string) => {
    dispatch(
      navigate({
        to: generatePath(QUICK_RECORDING_ROUTE, { prefix }),
      })
    );
  };

  return (
    <div className="sm:-mx-1 grid gap-5 sm:grid-cols-[repeat(auto-fill,minmax(320px,1fr))] grid-cols-[repeat(auto-fill,minmax(340px,1fr))]">
      {records.map((record) => {
        const { teamMembersPermission, userPermissionToVideoRecords } = record;

        const owner = userPermissionToVideoRecords.find(
          (uV) => uV.role === UserPermissionToVideoRecordRole.OWNER
        );

        const permissions = getRecordPermissions({
          userId: user.id,
          userPermissionToVideoRecords,
          teamMembersPermission,
        });

        const duration = formatMsToTimePassed(
          (record.metadata?.mux?.duration || 0) * 1000
        );

        return (
          <VideoCardPreview
            duration={duration}
            isFailed={checkIsRecordConvertingFailed({
              status: record.status,
              statusConvert: record.statusConvert,
            })}
            isLoading={checkIsRecordConverting(record.statusConvert)}
            onClick={() => handleOpen(record.prefix)}
            key={record.id}
            createdAt={record.createdAt}
            title={record.name}
            url={getMuxImage(record.playbackId || '')}
            Owner={
              category === RecordCategory.SHARED && (
                <div className="flex items-center gap-2 truncate">
                  <Avatar
                    displayName={owner?.user?.displayName || ''}
                    src={owner?.user?.photoUrl}
                    size="3xs"
                    isUserAvatar
                    className="border-light-7 border-[0.5px] !bg-base-white !text-xxs"
                  />
                  <div className="text-xs text-gray-700 truncate">
                    by{' '}
                    <span className="font-semibold">
                      {owner?.user?.displayName || ''}
                    </span>
                  </div>
                </div>
              )
            }
            PermissionStatus={
              category === RecordCategory.MY ? (
                <VideoStatus
                  isTeamAccessible={[
                    VideoRecordTeamMembersPermission.VIEW,
                    VideoRecordTeamMembersPermission.EDIT,
                  ].includes(record.teamMembersPermission)}
                  teamMembersCount={record.userPermissionToVideoRecords.length}
                />
              ) : (
                <VideoStatusShared isCanEdit={permissions.isCanEdit} />
              )
            }
            Action={
              <div className="md:hidden">
                <RecordDropdown
                  record={record}
                  isShowCopyLink
                  isShowRename
                  view="grid"
                />
              </div>
            }
          />
        );
      })}
    </div>
  );
};

export const RecordsList: FC<Props> = ({ records, category }) => {
  const user = useSelector(authUserModel.selectors.selectUserWithError);
  const dispatch = useDispatch();

  return (
    <div className="sm:-mx-3 overflow-x-auto -mx-2 px-2 pb-2 -mb-2">
      <Table className="table-fixed">
        <TableHeader>
          <TableHeaderCell className="sm:w-66 w-135">Name</TableHeaderCell>
          <TableHeaderCell className="w-34">Duration</TableHeaderCell>
          <TableHeaderCell className="w-34">Permissions</TableHeaderCell>
          <TableHeaderCell className="w-34">Created</TableHeaderCell>
          <TableHeaderCell className="w-14">Owner</TableHeaderCell>
          <TableHeaderCell className="w-15" />
        </TableHeader>
        {records.map((record) => {
          const { teamMembersPermission, userPermissionToVideoRecords } =
            record;

          const owner = userPermissionToVideoRecords.find(
            (uV) => uV.role === UserPermissionToVideoRecordRole.OWNER
          );

          const permissions = getRecordPermissions({
            userId: user.id,
            userPermissionToVideoRecords,
            teamMembersPermission,
          });

          const duration = formatMsToTimePassed(
            (record.metadata?.mux?.duration || 0) * 1000
          );

          return (
            <>
              <TableRow key={record.id + 'gap'} className="h-2" />
              <TableRow
                key={record.id}
                onClick={() =>
                  dispatch(
                    navigate({
                      to: generatePath(QUICK_RECORDING_ROUTE, {
                        prefix: record.prefix,
                      }),
                    })
                  )
                }
              >
                <TableCell>
                  <VideoCardTablePreview
                    isFailed={checkIsRecordConvertingFailed({
                      status: record.status,
                      statusConvert: record.statusConvert,
                    })}
                    isLoading={checkIsRecordConverting(record.statusConvert)}
                    url={getMuxImage(record.playbackId || '')}
                    name={record.name}
                  />
                </TableCell>
                <TableCell>{duration}</TableCell>
                <TableCell>
                  {category === RecordCategory.MY ? (
                    <VideoStatus
                      isTeamAccessible={[
                        VideoRecordTeamMembersPermission.VIEW,
                        VideoRecordTeamMembersPermission.EDIT,
                      ].includes(record.teamMembersPermission)}
                      teamMembersCount={
                        record.userPermissionToVideoRecords.length
                      }
                    />
                  ) : (
                    <VideoStatusShared isCanEdit={permissions.isCanEdit} />
                  )}
                </TableCell>
                <TableCell>
                  <TimeAgo datetime={record.createdAt} locale="my" />
                </TableCell>
                <TableCell>
                  <div className="flex justify-start">
                    <Tooltip
                      sideOffset={4}
                      className="px-3 py-2"
                      hideArrow
                      trigger={
                        <Avatar
                          className="border border-base-black/[0.08]"
                          isUserAvatar
                          displayName={owner?.user?.displayName || ''}
                          src={owner?.user?.photoUrl}
                          size="xs"
                        />
                      }
                    >
                      <AvatarTooltipContent
                        displayName={owner?.user?.displayName}
                        email={owner?.user?.email || ''}
                      />
                    </Tooltip>
                  </div>
                </TableCell>
                <TableCell className="text-end">
                  <div className="flex justify-end md:hidden">
                    <RecordDropdown
                      record={record}
                      isShowCopyLink
                      isShowRename
                      view="list"
                    />
                  </div>
                </TableCell>
              </TableRow>
            </>
          );
        })}
      </Table>
    </div>
  );
};
